import { Component, ReactNode, ErrorInfo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/* ------------------------------------------------------------------ */
//  Simple in-memory error log (can be swapped for Sentry)
/* ------------------------------------------------------------------ */
const ERROR_LOG: { message: string; stack?: string; time: string; info?: string }[] = [];

export function logError(error: Error, info?: ErrorInfo) {
  const entry = {
    message: error.message,
    stack: error.stack,
    time: new Date().toISOString(),
    info: info?.componentStack?.slice(0, 500),
  };
  ERROR_LOG.push(entry);
  // Send to console for now; replace with Sentry/other in production
  console.error("[ErrorBoundary]", entry);

  // Optional: send to a lightweight edge function endpoint
  try {
    if (typeof navigator !== "undefined" && navigator.onLine) {
      fetch("/functions/v1/log-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // silent
  }
}

export function getRecentErrors() {
  return ERROR_LOG.slice(-20);
}

/* ------------------------------------------------------------------ */
//  Error Boundary Component
/* ------------------------------------------------------------------ */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logError(error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({
  error,
  onReset,
}: {
  error?: Error;
  onReset: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="max-w-md w-full p-8 rounded-2xl border-border/50 text-center space-y-5">
        <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
          <AlertTriangle className="h-7 w-7 text-destructive" />
        </div>
        <div>
          <h2 className="text-lg font-display font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We ran into an unexpected issue. Your data is safe.
          </p>
        </div>
        {error && (
          <div className="bg-secondary/40 rounded-xl p-3 text-left">
            <p className="text-xs font-mono text-muted-foreground truncate">
              {error.message}
            </p>
          </div>
        )}
        <div className="flex gap-2 justify-center">
          <Button onClick={onReset} variant="outline" className="gap-1.5 rounded-xl">
            <RotateCcw className="h-4 w-4" />
            Try Again
          </Button>
          <a href="/dashboard">
            <Button className="gap-1.5 rounded-xl">
              <Home className="h-4 w-4" />
              Home
            </Button>
          </a>
        </div>
      </Card>
    </div>
  );
}
